import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateInmopublicAccountMutation } from '../inmopublicAccountsApi';
import { Plus } from 'lucide-react';
import {
  createAccountSchema,
  type CreateAccountFormData,
  type CreateAccountResponse,
} from './CreateAccountDialog.types';

export const CreateAccountDialog = () => {
  const [open, setOpen] = useState(false);
  const [createdAccount, setCreatedAccount] = useState<CreateAccountResponse | null>(null);

  const [createAccount, { isLoading }] = useCreateInmopublicAccountMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<CreateAccountFormData>({
    resolver: zodResolver(createAccountSchema),
    defaultValues: {
      name: '',
      logo: '',
      municipality: '',
      status: 'active',
      primaryDomain: '',
    },
  });

  const onSubmit = async (data: CreateAccountFormData) => {
    try {
      const submitData = {
        name: data.name.trim(),
        logo: data.logo?.trim(),
        municipality: data.municipality.trim(),
        status: data.status,
        primaryDomain: data.primaryDomain?.trim() || undefined,
      };

      const result = await createAccount(submitData).unwrap();
      setCreatedAccount(result);
    } catch (error: any) {
      console.error('Error creating account:', error);
      if (error?.data?.message?.includes('name already exists')) {
        setError('name', { message: 'Account with this name already exists' });
      } else {
        setError('name', { message: 'Error creating account. Please try again.' });
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    reset();
    setCreatedAccount(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className='bg-blue-600 hover:bg-blue-700 text-white'>
          <Plus className='h-4 w-4 mr-2' />
          Create Account
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px] bg-slate-800 border-slate-700'>
        <DialogHeader>
          <DialogTitle className='text-white'>
            {createdAccount ? 'Account Created Successfully' : 'Create New Account'}
          </DialogTitle>
        </DialogHeader>

        {createdAccount ? (
          <div className='space-y-6'>
            <div className='bg-green-900/20 border border-green-700 rounded-lg p-4'>
              <h3 className='text-green-400 font-medium mb-2'>Account Created Successfully!</h3>
              <div className='space-y-2 text-sm'>
                <p>
                  <span className='text-slate-400'>Name:</span>{' '}
                  <span className='text-white'>{createdAccount.account.name}</span>
                </p>
                <p>
                  <span className='text-slate-400'>Municipality:</span>{' '}
                  <span className='text-white'>{createdAccount.account.municipality}</span>
                </p>
                <p>
                  <span className='text-slate-400'>Status:</span>{' '}
                  <span className='text-white'>{createdAccount.account.status}</span>
                </p>
                {createdAccount.account.primaryDomain && (
                  <p>
                    <span className='text-slate-400'>Primary Domain:</span>{' '}
                    <span className='text-white'>{createdAccount.account.primaryDomain}</span>
                  </p>
                )}
                <p>
                  <span className='text-slate-400'>Account ID:</span>{' '}
                  <span className='text-white font-mono text-xs'>{createdAccount.account.id}</span>
                </p>
              </div>
            </div>

            <div className='flex justify-end'>
              <Button onClick={handleClose} className='bg-blue-600 hover:bg-blue-700 text-white'>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='name' className='text-slate-300'>
                Account Name *
              </Label>
              <Input
                id='name'
                {...register('name')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='My Company'
              />
              {errors.name && <p className='text-red-400 text-sm'>{errors.name.message}</p>}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='logo' className='text-slate-300'>
                Logo URL *
              </Label>
              <Input
                id='logo'
                {...register('logo')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='https://example.com/logo.png'
              />
              {errors.logo && <p className='text-red-400 text-sm'>{errors.logo.message}</p>}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='municipality' className='text-slate-300'>
                Municipality *
              </Label>
              <Input
                id='municipality'
                {...register('municipality')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='Madrid'
              />
              {errors.municipality && <p className='text-red-400 text-sm'>{errors.municipality.message}</p>}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='status' className='text-slate-300'>
                Status
              </Label>
              <select
                id='status'
                {...register('status')}
                className='w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                <option value='active'>Active</option>
                <option value='inactive'>Inactive</option>
                <option value='suspended'>Suspended</option>
                <option value='trial'>Trial</option>
              </select>
              {errors.status && <p className='text-red-400 text-sm'>{errors.status.message}</p>}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='primaryDomain' className='text-slate-300'>
                Primary Domain
              </Label>
              <Input
                id='primaryDomain'
                {...register('primaryDomain')}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                placeholder='https://mycompany.com'
              />
              {errors.primaryDomain && <p className='text-red-400 text-sm'>{errors.primaryDomain.message}</p>}
            </div>

            <div className='flex justify-end space-x-2 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                disabled={isLoading}
                className='border-slate-600 text-slate-300 hover:bg-slate-700'
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isLoading} className='bg-blue-600 hover:bg-blue-700 text-white'>
                {isLoading ? 'Creating...' : 'Create Account'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
